import type { CSSProperties } from 'react';
import { useT } from '../i18n/I18nProvider';
import type { TKey } from '../i18n/dictionary';
import type { ScreenModel } from '../domain/resolveScreen';
import { Icon } from './icons';

/** Banner destinace – „Kam balík jede?“ (spec 2, vlastní vizuální kanál). */
export function TransferBanner({ model }: { model: ScreenModel }) {
  const { t } = useT();
  if (!model.banner) return null;
  const onStop = model.tier === 'stop';

  return (
    <div
      className={`transfer${onStop ? ' onstop' : ''}`}
      style={{ '--t-bg': model.banner.bg } as CSSProperties}
    >
      <div className="trow">
        <Icon name="border" />
        {t(model.banner.textKey)}
      </div>
      {model.banner.loadToday && <span className="load">{t('loadToday')}</span>}
    </div>
  );
}

/** Okno s datem – tři podoby podle převozu a existence data (spec 3). */
export function UntilBox({ model }: { model: ScreenModel }) {
  const { t } = useT();
  const box = model.untilBox;
  if (!box) return null;

  return (
    <div className="until" style={{ '--u-bg': box.bg } as CSSProperties}>
      <Icon name={box.icon} />
      <span>
        <span className="lbl">
          {t(box.labelKey)}
          {box.warehouseCode && <span className="whcode">{box.warehouseCode}</span>}
        </span>
        <span className="d">{box.value ?? (box.valueKey ? t(box.valueKey) : '')}</span>
      </span>
    </div>
  );
}

/** Verdikt – to, co musí být vidět z jednoho metru. */
export function Verdict({ model }: { model: ScreenModel }) {
  const { t } = useT();
  const v = model.verdict;
  const name = t(`s.${model.stateId}.name` as TKey);
  const headline = t(`s.${model.stateId}.headline` as TKey);
  const sub = t(`s.${model.stateId}.sub` as TKey);

  return (
    <div
      className={`verdict${model.tokens.solid ? ' solid' : ''}`}
      style={
        {
          '--accent': v.accent,
          '--v-bg': model.tokens.vBg,
          '--v-ink': model.tokens.vInk,
          ...(model.tokens.vIco ? { '--v-ico': model.tokens.vIco } : {}),
        } as CSSProperties
      }
      role="status"
      aria-live="polite"
    >
      <span className="v-ico">
        <Icon name={v.icon} />
      </span>
      <span>
        <span className="v-kicker">{t(v.kickerKey)}</span>

        {v.useStateName ? (
          <span className="v-state">{name}</span>
        ) : (
          <span className="v-state" style={v.warnHeadline ? { color: v.accent } : undefined}>
            {headline}
          </span>
        )}

        {v.showSub && (
          <span className="v-sub">
            {v.showNameInSub && (
              <>
                <b>{name}</b> ·{' '}
              </>
            )}
            {sub}
          </span>
        )}
      </span>
    </div>
  );
}

export function Chips({ model }: { model: ScreenModel }) {
  const { t } = useT();
  if (model.chips.length === 0) return null;
  const onStop = model.tier === 'stop';

  return (
    <div className="chips">
      {model.chips.map((chip) => (
        <span
          key={chip.key}
          className={`chip${chip.variant === 'part' ? ' part' : ''}${onStop ? ' onstop' : ''}`}
        >
          <Icon name={chip.icon} />
          {t(chip.key)}
        </span>
      ))}
    </div>
  );
}

/** Vrstva NOTFOUND: žádná karta, jen naskenovaný kód pod neutrálním labelem. */
export function CodeBox({ code }: { code: string }) {
  const { t } = useT();
  return (
    <div className="codebox">
      <div className="k">{t('scanned')}</div>
      <code>{code}</code>
    </div>
  );
}

export function TempBand({ model }: { model: ScreenModel }) {
  const { t } = useT();
  const band = model.tempBand;
  if (!band) return null;

  const comp = band.compose;
  const text = comp
    ? [
        comp.chilled > 0 ? `${comp.chilled}× ${t('chill')}` : null,
        comp.frozen > 0 ? `${comp.frozen}× ${t('frozen')}` : null,
      ]
        .filter(Boolean)
        .join(' + ')
    : t(band.icon === 'snow' ? 'frozen' : 'chill');

  return (
    <div className="temp" style={{ '--tm-bg': band.bg, '--tm-ink': band.ink } as CSSProperties}>
      <Icon name={band.icon} />
      {text}
    </div>
  );
}

export function CompletionBand({ model }: { model: ScreenModel }) {
  const { t } = useT();
  const band = model.completionBand;
  if (!band) return null;

  return (
    <div
      className="komp"
      style={
        {
          '--kb': band.complete ? '#E6F6EE' : '#FEF7C3',
          '--ki': band.complete ? '#046C4E' : '#7A4A02',
        } as CSSProperties
      }
    >
      <Icon name={band.complete ? 'check' : 'warn'} />
      <span>
        <span className="kk">{t('kompLbl')}</span>
        <span className="kv">
          {band.complete ? t('kompDone') : `${t('kompTodo')}: ${band.missing.join(', ')}`}
        </span>
      </span>
    </div>
  );
}
